/**
 * ONE strip per page, under the page header, saying the page's worst state — the designer's
 * brief part 1 §4 (DEC-85, DEC-95), `docs/adr/9033`.
 *
 * A page used to say its states where each read failed or came back absent: the store × article
 * drill showed four notices in four panels, the Cockpit about twenty. Now pages and panels
 * REGISTER their states with the page (`usePageStatusEntry`) and the page draws one strip: the
 * most severe state, and at its inline end «e altri N», which opens the list of the others.
 *
 *   error          ink, white text, «!»; «Riprova» — the only action; data already read stays
 *   no-permission  white, 1px ink border, a padlock; «Puoi leggere, non decidere.»
 *   in-progress    `surface-constants`, a 3px ink rule at the inline start, «◐»
 *   awaiting       the same surface without the rule — ADR 9013's 200-with-nothing-in-it
 *   not-published  the hatch and the «In arrivo» tag
 *   notice         white, 1px hairline, an (i); what the page did for the planner, said once —
 *                  what the page did on its own (DEC-99, ADR 9039)
 *
 * The order is the designer's hierarchy (error › no permission › in progress › not published),
 * with ADR 9013's `awaiting` placed after `in-progress`: both say "not yet", and a run in flight
 * is the more specific of the two.
 *
 * **No estimated end is shown.** The designer draws «Fine stimata alle {ora}», and the platform
 * publishes no estimated end for a cycle in flight (bata-platform#471, DEC-95). `estimatedEnd`
 * exists so the sentence can appear the day a published field feeds it; nothing here computes one.
 *
 * **Opt in, then adopt.** Outside a `PageStatusProvider`, `usePageStatusEntry` returns `false`
 * and every adapted notice (`ProblemNotice`, `AwaitingDataStrip`, `NotPublishedLine`) renders as it
 * always has. Inside one, it registers and renders nothing. So pages convert one at a time
 * (C6/C7), and the router can mount the provider for every page once they all have.
 *
 * The strip is ONE live region (`role="status"`), mounted while the provider is, so a state that
 * arrives after the page is announced; it turns `aria-live="assertive"` while an error leads.
 */

import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Info, Lock } from 'lucide-react';

import { problemMessage } from '../api';
import type { ICUValues } from '../primitives';
import { FOCUS_RING } from '../primitives';
import { ARRIVING_HATCH, ArrivingTag } from './arriving';

// ── the entries ──────────────────────────────────────────────────────────────

/** A catalogue sentence: a key and its ICU arguments. */
export interface PageStatusSentence {
  readonly key: string;
  readonly values?: ICUValues;
}

export type PageStatusKind = 'error' | 'no-permission' | 'in-progress' | 'awaiting' | 'not-published' | 'notice';

export type PageStatusEntry =
  | {
    readonly kind: 'error';
    /** What could not be read — "The store's lines could not be loaded." */
    readonly message: PageStatusSentence;
    /** The thrown value; its catalogue message is listed under «e altri N». */
    readonly error?: unknown;
    /** Read again. The strip's «Riprova» calls every registered error's `retry`. */
    readonly retry?: () => unknown;
  }
  | {
    readonly kind: 'no-permission';
    /** The role the act needs, as the platform names it — «Serve il ruolo {role}.» */
    readonly role?: string;
    /** Defaults to «Puoi leggere, non decidere.» */
    readonly message?: PageStatusSentence;
    /** Defaults to the role sentence when `role` is given, else nothing. */
    readonly detail?: PageStatusSentence;
  }
  | {
    readonly kind: 'in-progress';
    /** The run in flight. The page shows the run before it. */
    readonly runId: string;
    /**
     * A PUBLISHED estimated end, when the platform publishes one. It does not today
     * (bata-platform#471): pass nothing, and the sentence has no time in it.
     */
    readonly estimatedEnd?: { readonly iso: string; readonly timeZone: string };
  }
  | { readonly kind: 'awaiting'; readonly message: PageStatusSentence; readonly detail?: PageStatusSentence }
  | { readonly kind: 'not-published'; readonly message: PageStatusSentence; readonly detail?: PageStatusSentence }
  /**
   * What the page did for the planner and says once (DEC-99, `docs/adr/9039`). The least severe
   * state.
   */
  | { readonly kind: 'notice'; readonly message: PageStatusSentence; readonly detail?: PageStatusSentence };

/** Most severe first. The designer's hierarchy, with ADR 9013's `awaiting` after `in-progress`. */
export const PAGE_STATUS_SEVERITY: Readonly<Record<PageStatusKind, number>> = {
  error: 4,
  'no-permission': 3,
  'in-progress': 2,
  awaiting: 1,
  'not-published': 0,
  notice: -1,
};

interface Registered {
  readonly id: string;
  /** Registration order — the tie-break among states of one severity. */
  readonly seq: number;
  readonly entry: PageStatusEntry;
}

/** The registered states, most severe first, earliest registered first within a severity. */
export function orderPageStatus<E extends { readonly seq: number; readonly entry: PageStatusEntry }>(
  entries: readonly E[],
): readonly E[] {
  return [...entries].sort((a, b) =>
    PAGE_STATUS_SEVERITY[b.entry.kind] - PAGE_STATUS_SEVERITY[a.entry.kind] || a.seq - b.seq);
}

// ── the registry ─────────────────────────────────────────────────────────────

interface Registry {
  readonly put: (id: string, entry: PageStatusEntry) => void;
  readonly drop: (id: string) => void;
}

const RegistryContext = createContext<Registry | null>(null);
const EntriesContext = createContext<readonly Registered[] | null>(null);

/**
 * Holds a page's states. Render `PageStatusStrip` inside it where the strip belongs — under the
 * header — or use `PageStatusRegion`, which does both.
 */
export function PageStatusProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [entries, setEntries] = useState<readonly Registered[]>([]);
  const seq = useRef(0);
  const put = useCallback((id: string, entry: PageStatusEntry) => {
    setEntries((current) => {
      const held = current.find((item) => item.id === id);
      if (held !== undefined) return current.map((item) => (item.id === id ? { ...item, entry } : item));
      seq.current += 1;
      return [...current, { id, seq: seq.current, entry }];
    });
  }, []);
  const drop = useCallback((id: string) => {
    setEntries((current) => (current.some((item) => item.id === id) ? current.filter((item) => item.id !== id) : current));
  }, []);
  const registry = useMemo(() => ({ put, drop }), [put, drop]);
  return (
    <RegistryContext.Provider value={registry}>
      <EntriesContext.Provider value={entries}>{children}</EntriesContext.Provider>
    </RegistryContext.Provider>
  );
}

/** Whether this component is inside a page that draws one strip. */
export function useInPageStatus(): boolean {
  return useContext(RegistryContext) !== null;
}

/**
 * Registers `entry` with the page's strip while the caller is mounted and `entry` is not
 * `null`. Returns `true` when the page took it — the caller then renders nothing of its own —
 * and `false` outside a `PageStatusProvider`, where the caller keeps today's notice.
 */
export function usePageStatusEntry(entry: PageStatusEntry | null): boolean {
  const registry = useContext(RegistryContext);
  const id = useId();
  const latest = useRef(entry);
  latest.current = entry;
  const signature = entry === null ? null : signatureOf(entry);

  useEffect(() => {
    const current = latest.current;
    if (registry === null || signature === null || current === null) return undefined;
    // The retry is read through the ref, so a new callback on every render does not re-register.
    registry.put(id, current.kind === 'error' && current.retry !== undefined
      ? {
        ...current,
        retry: () => {
          const now = latest.current;
          return now !== null && now.kind === 'error' ? now.retry?.() : undefined;
        },
      }
      : current);
    return () => registry.drop(id);
  }, [registry, id, signature]);

  return registry !== null && entry !== null;
}

/** What makes two entries the same statement: everything but the callback, the error by its message. */
function signatureOf(entry: PageStatusEntry): string {
  if (entry.kind === 'error') {
    return JSON.stringify({
      kind: entry.kind,
      message: entry.message,
      problem: entry.error === undefined ? null : problemMessage(entry.error),
      retry: entry.retry !== undefined,
    });
  }
  return JSON.stringify(entry);
}

// ── the strip ────────────────────────────────────────────────────────────────

/** The provider and the strip, the strip first — a page's body in one wrapper. */
export function PageStatusRegion({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return (
    <PageStatusProvider>
      <PageStatusStrip />
      {children}
    </PageStatusProvider>
  );
}

const SURFACE: Readonly<Record<PageStatusKind, string>> = {
  error: 'bg-strip-error text-strip-error-fg',
  'no-permission': 'bg-surface-raised text-ink-primary border-ink-primary border',
  'in-progress': 'bg-surface-constants text-ink-primary border-ink-primary border-s-[3px]',
  awaiting: 'bg-surface-constants text-ink-primary',
  'not-published': `${ARRIVING_HATCH} text-ink-primary`,
  notice: 'bg-surface-raised text-ink-primary border-hairline border',
};

/**
 * The second sentence's ink. On `surface-constants` and on the hatch, `ink-secondary` measures
 * 4.46:1 in light — under AA — so those strips keep `ink-primary` and let the weight (600 against
 * 400) tell the strong part from the rest.
 */
const SECONDARY: Readonly<Record<PageStatusKind, string>> = {
  error: 'text-strip-error-fg-secondary',
  'no-permission': 'text-ink-secondary',
  'in-progress': 'text-ink-primary',
  awaiting: 'text-ink-primary',
  'not-published': 'text-ink-primary',
  notice: 'text-ink-secondary',
};

/**
 * The page's one strip. Nothing registered: an empty live region, out of the flow (so the
 * page's gap does not open for it) but in the accessibility tree, so what registers later is
 * announced. Outside a provider it renders nothing.
 */
export function PageStatusStrip(): React.JSX.Element | null {
  const { t } = useTranslation();
  const entries = useContext(EntriesContext);
  const [open, setOpen] = useState(false);
  const listId = useId();
  const ordered = useMemo(() => orderPageStatus(entries ?? []), [entries]);
  if (entries === null) return null;

  const top = ordered[0];
  const others = ordered.length - 1;
  if (top === undefined) {
    return <div role="status" aria-live="polite" data-page-status="none" className="sr-only" />;
  }
  const kind = top.entry.kind;
  const retries = ordered.flatMap(({ entry }) => (entry.kind === 'error' && entry.retry !== undefined ? [entry.retry] : []));

  return (
    <div className="flex flex-col" data-page-status={kind} data-tour="page-status">
      <div
        role="status"
        aria-live={kind === 'error' ? 'assertive' : 'polite'}
        aria-atomic="true"
        className={`${SURFACE[kind]} flex min-h-[44px] items-center gap-3 px-4 py-2 text-[13px] leading-5`}
      >
        <StatusLine entry={top.entry} />
        {others > 0 && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => setOpen((was) => !was)}
            className={`${SECONDARY[kind]} shrink-0 text-[12px] underline-offset-2 hover:underline ${FOCUS_RING}`}
          >
            {t('pageStatus.others', { count: others })}
          </button>
        )}
        {kind === 'error' && retries.length > 0 && (
          <button
            type="button"
            onClick={() => { for (const retry of retries) retry(); }}
            className={`border-strip-error-fg shrink-0 border px-3 py-1 font-semibold ${FOCUS_RING}`}
          >
            {t('action.retry')}
          </button>
        )}
      </div>
      {others > 0 && open && (
        <ul id={listId} aria-label={t('pageStatus.listLabel')} className="border-hairline bg-surface-raised flex flex-col border border-t-0">
          {ordered.map(({ id, entry }) => (
            <li key={id} className="border-hairline flex min-h-[44px] items-center gap-3 border-b px-4 text-[13px] leading-5 last:border-b-0">
              <StatusLine entry={entry} listed />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The glyph, the strong sentence and the second one — on at most two lines, never cut without a
 * way to read the rest (usability audit D6: a message up to 359 characters long was drawn on ONE
 * line with an ellipsis and no tooltip).
 *
 * - **An error is never clamped.** It is the one message the planner must read whole, so it
 *   wraps onto as many lines as it needs.
 * - **Anything else is clamped at two lines**, the whole message in the `title`, and — when the
 *   clamp actually cuts it (measured, `scrollHeight > clientHeight`, again on every resize) — a
 *   «Mostra tutto» button that opens it in place, for a keyboard or touch user a title never
 *   reaches.
 */
function StatusLine({ entry, listed = false }: {
  readonly entry: PageStatusEntry;
  /** In the list: the secondary ink is the raised surface's, and an error names its problem. */
  readonly listed?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { strong, detail } = sentencesOf(entry, i18n.language);
  const secondary = listed ? 'text-ink-secondary' : SECONDARY[entry.kind];
  const problem = listed && entry.kind === 'error' && entry.error !== undefined ? problemMessage(entry.error) : null;
  const strongText = t(strong.key, strong.values ?? {});
  const detailText = detail === null ? null : t(detail.key, detail.values ?? {});
  const problemText = problem === null ? null : t(problem.key, problem.values);
  const whole = [strongText, detailText, problemText].filter((part): part is string => part !== null).join(' ');
  const clampable = entry.kind !== 'error';
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const [element, setElement] = useState<HTMLParagraphElement | null>(null);
  useLayoutEffect(() => {
    if (element === null || !clampable || expanded) return undefined;
    const measure = (): void => setClamped(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, whole, clampable, expanded]);
  return (
    <>
      <Glyph kind={entry.kind} />
      <p
        ref={setElement}
        data-status-message=""
        className={`min-w-0 flex-1 [overflow-wrap:anywhere] ${clampable && !expanded ? 'line-clamp-2' : ''}`}
        {...(clampable ? { title: whole } : {})}
      >
        <strong className="font-semibold">{strongText}</strong>
        {detailText !== null && <span className={`${secondary} ms-2`}>{detailText}</span>}
        {problemText !== null && <span className={`${secondary} ms-2`}>{problemText}</span>}
      </p>
      {clampable && (clamped || expanded) && (
        <button
          type="button"
          aria-expanded={expanded}
          data-status-expand=""
          onClick={() => setExpanded((was) => !was)}
          className={`${secondary} shrink-0 text-[12px] underline underline-offset-2 ${FOCUS_RING}`}
        >
          {t(expanded ? 'pageStatus.showLess' : 'pageStatus.showAll')}
        </button>
      )}
    </>
  );
}

function Glyph({ kind }: { readonly kind: PageStatusKind }): React.JSX.Element {
  switch (kind) {
    case 'error':
      return <span aria-hidden className="font-numeric shrink-0 font-semibold">!</span>;
    case 'no-permission':
      return <Lock size={14} aria-hidden className="shrink-0" />;
    case 'in-progress':
      return <span aria-hidden className="font-numeric shrink-0 font-semibold">◐</span>;
    case 'awaiting':
      return <Clock size={14} aria-hidden className="shrink-0" />;
    case 'not-published':
      return <ArrivingTag />;
    case 'notice':
      return <Info size={14} aria-hidden className="shrink-0" />;
  }
}

/** The two sentences an entry says, as catalogue keys — pure, so the defaults are testable. */
export function sentencesOf(entry: PageStatusEntry, language: string): {
  readonly strong: PageStatusSentence;
  readonly detail: PageStatusSentence | null;
} {
  switch (entry.kind) {
    case 'error':
      return { strong: entry.message, detail: { key: 'pageStatus.error.kept' } };
    case 'no-permission':
      return {
        strong: entry.message ?? { key: 'pageStatus.noPermission.title' },
        detail: entry.detail ?? (entry.role === undefined ? null : { key: 'pageStatus.noPermission.role', values: { role: entry.role } }),
      };
    case 'in-progress':
      return {
        strong: { key: 'pageStatus.inProgress.title', values: { run: entry.runId } },
        detail: entry.estimatedEnd === undefined
          ? { key: 'pageStatus.inProgress.previous' }
          : {
            key: 'pageStatus.inProgress.previousWithEnd',
            values: { time: formatTime(entry.estimatedEnd.iso, language, entry.estimatedEnd.timeZone) },
          },
      };
    case 'awaiting':
    case 'not-published':
    case 'notice':
      return { strong: entry.message, detail: entry.detail ?? null };
  }
}

function formatTime(iso: string, language: string, timeZone: string): string {
  const instant = new Date(iso);
  return Number.isNaN(instant.getTime())
    ? iso
    : new Intl.DateTimeFormat(language, { timeStyle: 'short', timeZone }).format(instant);
}
